import React, { useMemo, useRef, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { usePlanDetails } from '../../hooks/dialogs/usePlanDetails';
import { IExerciseGroup } from '../../types/workout/exercise-group';
import { ExerciseGroupType } from '../../types/workout/group-type';
import VideoDialog from './VideoDialog';

import PlanDetailsHeader from './plan-details/PlanDetailsHeader';
import MetaStrip from './plan-details/MetaStrip';
import FeedbackStrip from './plan-details/FeedbackStrip';
import GroupNav from './plan-details/GroupNav';
import ExerciseCard from './plan-details/ExerciseCard';
import RestCard from './plan-details/RestCard';
import GroupSeparator from './plan-details/GroupSeparator';
import EditSessionForm from './plan-details/EditSessionForm';
import PlanDetailsFooter from './plan-details/PlanDetailsFooter';
import { PD } from './plan-details/tokens';

interface PlanDetailsProps {
  planId: number;
  visible: boolean;
  setPlanDetailsVisible: (_visible: boolean) => void;
  setRefreshKey: (_key: ((_prev: number) => number) | number) => void;
  setLoading: (_loading: boolean) => void;
  isTemplate: boolean;
  clientId: string;
}

type CardItem =
  | { kind: 'sep'; key: string }
  | { kind: 'rest'; key: string; duration?: number; groupIdx: number }
  | {
      kind: 'ex';
      key: string;
      groupIdx: number;
      groupName: string;
      posInGroup: number;
      totalInGroup: number;
      exercise: IExerciseGroup['exercises'][number];
    };

function isRest(g: IExerciseGroup): boolean {
  return g.groupType === ExerciseGroupType.REST || g.isRestPeriod === true;
}

const PlanDetails: React.FC<PlanDetailsProps> = ({
  planId,
  visible,
  setPlanDetailsVisible,
  setRefreshKey,
  setLoading,
  isTemplate,
  clientId
}) => {
  const {
    workoutPlan,
    rpeMethod,
    videoDialogVisible,
    selectedVideo,
    isEditing,
    editedLocation,
    editedContactMethod,
    editedNotes,
    editedSessionTime,
    editedSessionMode,
    user,
    setVideoDialogVisible,
    setIsEditing,
    setEditedLocation,
    setEditedContactMethod,
    setEditedNotes,
    setEditedSessionTime,
    setEditedSessionMode,
    handleEdit,
    handleDelete,
    handleStartWorkout,
    handleVideoClick,
    handleSaveChanges,
    handleCancelEdit,
    canStartWorkout
  } = usePlanDetails({
    planId,
    isTemplate,
    clientId,
    setLoading,
    setPlanDetailsVisible,
    setRefreshKey
  });

  const isCoach = user?.userType === 'coach';
  const isClient = user?.userType === 'client';
  const status = workoutPlan.status || '';
  const completedView = !isTemplate && status === 'completed';
  const canEditDelete = isCoach && !isEditing && (isTemplate || status === 'pending');
  const canStart = canStartWorkout() && !isEditing;
  const canEditSession = !isTemplate && isCoach && status !== 'completed' && !isEditing;

  const title = isTemplate
    ? workoutPlan.workoutTemplate?.planName || ''
    : workoutPlan.instanceName || workoutPlan.workoutTemplate?.planName || '';

  // Build a flat list of cards (exercises + separators + rest cards).
  // Keep a ref by groupIdx → DOM node so GroupNav can scroll to the first card
  // belonging to that group via scrollIntoView.
  const cards = useMemo<CardItem[]>(() => {
    const out: CardItem[] = [];
    const groups = workoutPlan.groups || [];
    groups.forEach((g, gi) => {
      if (gi > 0) {
        const prev = groups[gi - 1];
        if (prev && !isRest(g) && !isRest(prev)) out.push({ kind: 'sep', key: `sep-${gi}` });
      }
      if (isRest(g)) {
        out.push({ kind: 'rest', key: `rest-${gi}`, duration: g.restDuration, groupIdx: gi });
      } else {
        (g.exercises || []).forEach((ex, ei) => {
          out.push({
            kind: 'ex',
            key: `ex-${gi}-${ei}-${ex.id}`,
            groupIdx: gi,
            groupName: g.name || `Bloque ${gi + 1}`,
            posInGroup: ei,
            totalInGroup: (g.exercises || []).length,
            exercise: ex
          });
        });
      }
    });
    return out;
  }, [workoutPlan.groups]);

  const scrollerRef = useRef<HTMLDivElement | null>(null);
  // First DOM node per groupIdx (used by GroupNav.onJump).
  const groupAnchorsRef = useRef<Record<number, HTMLDivElement | null>>({});
  const [activeGroupIdx, setActiveGroupIdx] = useState(0);

  const handleJumpToGroup = (idx: number) => {
    setActiveGroupIdx(idx);
    const node = groupAnchorsRef.current[idx];
    if (node && typeof node.scrollIntoView === 'function') {
      node.scrollIntoView({ inline: 'start', behavior: 'smooth', block: 'nearest' });
    }
  };

  const handleClose = () => setPlanDetailsVisible(false);
  const rpeLabel = rpeMethod?.name || 'RPE';
  const hasGroups = (workoutPlan.groups || []).length > 0;

  return (
    <>
      <Dialog
        visible={visible}
        onHide={handleClose}
        showHeader={false}
        dismissableMask
        modal
        className="pd-dialog-wrap"
        contentStyle={{ padding: 0 }}
        style={{ width: 880, maxWidth: '95vw' }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1, height: '100%' }}>
          <PlanDetailsHeader title={title} status={status} isTemplate={isTemplate} onClose={handleClose} />

          {!isEditing && (
            <MetaStrip
              session={workoutPlan.trainingSession}
              editable={canEditSession}
              onEdit={() => setIsEditing(true)}
            />
          )}

          {!isEditing && completedView && workoutPlan.feedback && <FeedbackStrip feedback={workoutPlan.feedback} />}

          {!isEditing && hasGroups && (
            <GroupNav groups={workoutPlan.groups || []} activeIdx={activeGroupIdx} onJump={handleJumpToGroup} />
          )}

          {/* Body */}
          {isEditing ? (
            <EditSessionForm
              sessionMode={editedSessionMode}
              setSessionMode={setEditedSessionMode}
              location={editedLocation}
              setLocation={setEditedLocation}
              contactMethod={editedContactMethod}
              setContactMethod={setEditedContactMethod}
              sessionTime={editedSessionTime}
              setSessionTime={setEditedSessionTime}
              notes={editedNotes}
              setNotes={setEditedNotes}
            />
          ) : (
            <div ref={scrollerRef} className="pd-scroller">
              <div className="pd-scroller-track">
                {cards.length === 0 ? (
                  <div
                    style={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: PD.ink3,
                      fontSize: 13,
                      minHeight: 200
                    }}
                  >
                    Sin ejercicios en este plan.
                  </div>
                ) : (
                  cards.map((c) => {
                    if (c.kind === 'sep') return <GroupSeparator key={c.key} />;
                    if (c.kind === 'rest') {
                      const anchorIdx = c.groupIdx;
                      return (
                        <RestCard
                          key={c.key}
                          duration={c.duration}
                          cardRef={(el) => {
                            if (!groupAnchorsRef.current[anchorIdx]) groupAnchorsRef.current[anchorIdx] = el;
                          }}
                        />
                      );
                    }
                    return (
                      <ExerciseCard
                        key={c.key}
                        exercise={c.exercise}
                        groupIdx={c.groupIdx}
                        groupName={c.groupName}
                        posInGroup={c.posInGroup}
                        totalInGroup={c.totalInGroup}
                        completedView={completedView}
                        rpeLabel={rpeLabel}
                        onPlay={handleVideoClick}
                        cardRef={(el) => {
                          // Only the FIRST card of each group is registered as the anchor —
                          // GroupNav.onJump scrolls to that node when the user taps a chip.
                          if (c.posInGroup === 0) groupAnchorsRef.current[c.groupIdx] = el;
                        }}
                      />
                    );
                  })
                )}
                <div style={{ flex: '0 0 8px' }} />
              </div>
            </div>
          )}

          <PlanDetailsFooter
            isEditing={isEditing}
            canEditDelete={Boolean(canEditDelete)}
            canStart={canStart}
            isClient={Boolean(isClient)}
            onCancel={handleCancelEdit}
            onSave={handleSaveChanges}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onStart={handleStartWorkout}
          />
        </div>
      </Dialog>

      <VideoDialog visible={videoDialogVisible} onHide={() => setVideoDialogVisible(false)} videoUrl={selectedVideo} />
    </>
  );
};

export default PlanDetails;
